import {
  broadCastChannelQuery,
  serviceWorkerQuery,
} from "../../../../libs/workers/serviceWorker";
import {
  CommunicationChannel,
  WorkerQueryOptions,
} from "../../../../libs/workers/types";

import { sendQueryResult } from "../../../../libs/workers/send";
import { isErrorMessage } from "../../../../libs/workers/utils";
import { STATS_BROADCAST_CHANNEL_NAME } from "./module";
import {
  StatsQuery,
  StatsQueryErrors,
  StatsQueryProps,
  StatsQueryResult,
  StatsQueryType,
} from "./types";

export async function sendStatsQueryResult<T extends StatsQueryType>(
  client: CommunicationChannel,
  data: StatsQueryResult<T>,
  type: T,
  request: { id: string; type: string; timestamp: number },
  opts?: WorkerQueryOptions
) {
  // console.debug(
  //   `📊 [SW::STATS | ${
  //     opts?.traceKey ?? "<untraced>"
  //   } ] Sending stats query result to client:`,
  //   { type, requestId: request.id, opts }
  // );
  return await sendQueryResult<T, StatsQueryResult<T>>(
    client,
    data,
    type,
    request,
    opts
  );
}

export function isStatsQuery(
  type: any,
  opts?: { verbose?: boolean }
): type is StatsQueryType {
  if (!type || typeof type !== "string") return false;

  const isValidQuery = Object.values(StatsQuery).includes(
    type as StatsQueryType
  );
  if (opts?.verbose) {
    console.log("Checking if type is a stats query:", type, isValidQuery);
  }

  return isValidQuery;
}

export function retryOnDatasourceNotReady<T extends StatsQueryType>(
  fn: () => Promise<StatsQueryResult<T> | null>,
  opts?: { maxRetries?: number; initialDelayMs?: number; traceKey?: string }
): Promise<StatsQueryResult<T> | null> {
  const retry = ({
    attempt,
    maxAttempts,
  }: {
    attempt: number;
    maxAttempts: number;
  }): Promise<StatsQueryResult<T> | null> => {
    async function onError(error: any, attempt: number, maxAttempts: number) {
      console.warn(
        `⚠️ [SW::STATS | ${opts?.traceKey} ] Stats query attempt ${attempt} failed:`,
        error
      );
      if (error.type === StatsQueryErrors.DATA_SOURCE_NOT_READY) {
        if (attempt <= maxAttempts) {
          console.warn(
            `⚠️ [SW::STATS | ${opts?.traceKey} ] BroadcastChannel query failed (attempt ${attempt}):`,
            error
          );
          await new Promise((resolve) => setTimeout(resolve, attempt * 1000));
          return await retry({ attempt: attempt + 1, maxAttempts });
        }
        return error;
      }
      return error;
    }
    return fn()
      .then(async (res) => {
        console.log(
          `✅ [SW::STATS | ${
            opts?.traceKey ?? "<untraced>"
          } ] Stats  query succeeded on attempt ${attempt}:`,
          { res }
        );
        if (isErrorMessage(res)) {
          const { error } = res;
          const result = await onError(error, attempt, maxAttempts);
          return result === error ? res : result;
        }

        return res;
      })
      .catch(async (error) => {
        console.error(
          `❌ [SW::STATS | ${
            opts?.traceKey ?? "<untraced>"
          } ] Stats BroadcastChannel query error:`,
          { error }
        );
        return await onError(error, attempt, maxAttempts);
      });
  };

  return retry({ attempt: 1, maxAttempts: 5 });
}

export function statsWorkerQuery<T extends StatsQueryType>(
  type: T,
  data: StatsQueryProps<T>,
  opts: WorkerQueryOptions = {}
) {
  const fn = () =>
    serviceWorkerQuery<StatsQueryResult<T>>({ type, data, opts });

  return retryOnDatasourceNotReady<T>(fn, {
    maxRetries: 5,
    traceKey: opts.traceKey,
  });
}

export function statsBroadcastChannelQuery<T extends StatsQueryType>(
  type: T,
  data: StatsQueryProps<T>,
  opts: WorkerQueryOptions = {}
) {
  const fn = () =>
    broadCastChannelQuery<StatsQueryResult<T>>(STATS_BROADCAST_CHANNEL_NAME, {
      type,
      data,
      opts,
    });

  return retryOnDatasourceNotReady<T>(fn, {
    maxRetries: 5,
    traceKey: opts.traceKey,
  });
}
